import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, ProductThumb, ProgressBar, StatusPill } from '@/components/common';
import type { ClaimCase } from '@/types/claim';
import { CLAIM_STEP_NAMES, claimStatus, claimStep, claimWhatsAppUrl } from '@/utils/claimCases';
import { formatDay } from '@/utils/productCases';

const OPEN_ALERT: Record<'draft' | 'submitted' | 'review', { title: string; body: string }> = {
  draft: { title: 'Finish your claim', body: 'Add the remaining details so we can start the review.' },
  submitted: { title: 'Claim submitted', body: 'We’ll start reviewing it shortly.' },
  review: { title: 'Claim under review', body: 'Our team is checking your photos. Usually takes 2 working days.' },
};

const CLOSED_ALERT = {
  approved: { title: 'Claim approved', body: 'Resolved by repair, replacement or store credit.' },
  rejected: { title: 'Claim not approved', body: 'Chat with us on WhatsApp if you have questions about the decision.' },
  cancelled: { title: 'Claim cancelled', body: 'This claim was withdrawn. You can file a new one any time.' },
} as const;

/** Claim summary on the Claims tab (Figma "Card/Claim"). Claims resolve only by repair, replacement or store credit. */
const ClaimCard: React.FC<{ claim: ClaimCase }> = ({ claim }) => {
  const navigate = useNavigate();
  const { tone, label } = claimStatus(claim);
  const step = claimStep(claim);
  const closed = claim.stage === 'closed';
  const subtitle = [claim.label, claim.planType].filter(Boolean).join(' · ');

  const alert = closed ? CLOSED_ALERT[claim.outcome ?? 'cancelled'] : OPEN_ALERT[claim.stage as keyof typeof OPEN_ALERT];
  const alertTone = closed ? (claim.outcome === 'rejected' ? 'danger' : claim.outcome === 'approved' ? 'success' : 'info') : claim.stage === 'draft' ? 'warning' : 'info';

  return (
    <article className="tw-flex tw-flex-col tw-gap-[14px] tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-p-5">
      <div className="tw-flex tw-items-center tw-gap-[14px]">
        <ProductThumb src={claim.imageUrl} alt={claim.productTitle} />
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-[3px]">
          <h3 className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">{claim.productTitle}</h3>
          <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">{subtitle}</p>
        </div>
        <StatusPill tone={tone}>{label}</StatusPill>
      </div>

      <div className="tw-flex tw-flex-col tw-gap-2">
        <ProgressBar
          value={step / CLAIM_STEP_NAMES.length}
          tone={closed && claim.outcome === 'approved' ? 'success' : 'info'}
          label="Claim progress"
        />
        <p className="tw-text-[12px] tw-font-semibold tw-leading-4 tw-text-ds-text-secondary">
          {closed
            ? `Closed · ${formatDay(claim.closedAt ?? claim.reviewedAt ?? claim.filedAt)}`
            : step === 0
              ? 'Not submitted yet'
              : `Step ${step} of ${CLAIM_STEP_NAMES.length} · ${CLAIM_STEP_NAMES[step - 1]}`}
        </p>
      </div>

      <Alert tone={alertTone} title={alert.title}>
        {alert.body}
      </Alert>

      {closed ? (
        <Button size="sm" fullWidth onClick={() => navigate(`/customer/claims/${claim.id}`)}>
          View summary
        </Button>
      ) : (
        <div className="tw-flex tw-gap-2.5">
          {claim.stage === 'draft' ? (
            <Button size="sm" className="tw-flex-1" onClick={() => navigate(`/customer/chat/${claim.id}`)}>
              Finish claim
            </Button>
          ) : (
            <Button size="sm" className="tw-flex-1" onClick={() => navigate(`/customer/claims/${claim.id}`)}>
              Track claim
            </Button>
          )}
          <Button size="sm" variant="secondary" className="tw-flex-1" href={claimWhatsAppUrl(claim)} target="_blank">
            Chat on WhatsApp
          </Button>
        </div>
      )}
    </article>
  );
};

export default ClaimCard;
